/**
 * All tunables in one place, sourced from Vite env vars (see .env.example).
 * Nothing here is secret in the traditional sense — a Maker key/secret pair
 * is meant to be loaded client-side per Mappedin's own docs — but keeping
 * them out of source lets each physical kiosk / venue ship its own .env
 * without touching code.
 */

function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `Missing ${name}. Copy .env.example to .env and fill in your Mappedin credentials.`,
    );
  }
  return value;
}

function numberOr(value: string | undefined, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function boolOr(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined) return fallback;
  return value.trim().toLowerCase() === "true";
}

/**
 * `?mode=img` (stand photos) / `?mode=box` (plain boxes, no photo requests) in
 * the page URL overrides VITE_SHOW_LOCATION_PHOTOS, so both versions can be
 * compared (or a low-bandwidth variant served) from one build.
 * `undefined` = no override.
 */
function photosUrlOverride(): boolean | undefined {
  if (typeof window === "undefined") return undefined;
  const v = new URLSearchParams(window.location.search).get("mode")?.trim().toLowerCase();
  if (v === "img") return true;
  if (v === "box") return false;
  return undefined;
}

/** Like `numberOr`, but returns `null` (not a fallback number) when unset, so "not configured" stays distinguishable from "configured as 0". */
function numberOrNull(value: string | undefined): number | null {
  if (value === undefined || value.trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function oneOf<T extends string>(value: string | undefined, options: readonly T[], fallback: T): T {
  const normalized = (value || "").trim().toLowerCase();
  return (options as readonly string[]).includes(normalized) ? (normalized as T) : fallback;
}

/**
 * Parses a comma-separated list of stand-color targets where several
 * consecutive targets can share one color by leaving it off every one but
 * the last, e.g. "1000-2000,101,107,F&B AREA HALL 2:#489FA8,2001-3000:#209056"
 * — the first four targets (a range, two single stand numbers, and a
 * named External ID) all get #489FA8, since none of them had their own
 * ":color" until the fourth; 2001-3000 gets its own #209056 immediately.
 * A target is a numeric range ("from-to"), a single stand number, or any
 * other string, taken as a literal External ID to match exactly (for a
 * named location like "F&B AREA HALL 2" that isn't in this venue's plain
 * numbering).
 */
function parseRangeColorPairs(value: string | undefined): {
  ranges: { min: number; max: number; color: string }[];
  ids: Record<string, string>;
} {
  const ranges: { min: number; max: number; color: string }[] = [];
  const ids: Record<string, string> = {};

  const applyColor = (target: string, color: string) => {
    const rangeMatch = target.match(/^(\d+)\s*-\s*(\d+)$/);
    if (rangeMatch) {
      ranges.push({ min: Number(rangeMatch[1]), max: Number(rangeMatch[2]), color });
      return;
    }
    const singleMatch = target.match(/^(\d+)$/);
    if (singleMatch) {
      const n = Number(singleMatch[1]);
      ranges.push({ min: n, max: n, color });
      return;
    }
    ids[target] = color;
  };

  let pendingTargets: string[] = [];
  for (const rawSegment of (value || "").split(",")) {
    const segment = rawSegment.trim();
    if (!segment) continue;
    const colonIndex = segment.indexOf(":");
    if (colonIndex === -1) {
      pendingTargets.push(segment);
      continue;
    }
    const target = segment.slice(0, colonIndex).trim();
    const color = segment.slice(colonIndex + 1).trim();
    if (!color) continue;
    if (target) pendingTargets.push(target);
    for (const t of pendingTargets) applyColor(t, color);
    pendingTargets = [];
  }
  return { ranges, ids };
}

/** Parses "Name:color,Name2:color2,..." into a lookup map — used for `categoryColors`, keyed by category name (must match exactly what's set in Mappedin admin). */
function parseNameColorPairs(value: string | undefined): Record<string, string> {
  const result: Record<string, string> = {};
  for (const rawSegment of (value || "").split(",")) {
    const segment = rawSegment.trim();
    if (!segment) continue;
    const colonIndex = segment.indexOf(":");
    if (colonIndex === -1) continue;
    const name = segment.slice(0, colonIndex).trim();
    const color = segment.slice(colonIndex + 1).trim();
    if (name && color) result[name] = color;
  }
  return result;
}

const env = import.meta.env;

// Referenced below by `standPhotoPosition`'s default, which follows
// whichever this was set to unless explicitly overridden.
const standAsBoxEnabled = boolOr(env.VITE_STAND_AS_BOX, false);

export const config = {
  mapId: required("VITE_MAPPEDIN_MAP_ID", env.VITE_MAPPEDIN_MAP_ID),
  key: required("VITE_MAPPEDIN_KEY", env.VITE_MAPPEDIN_KEY),
  secret: required("VITE_MAPPEDIN_SECRET", env.VITE_MAPPEDIN_SECRET),

  /**
   * "offline" (default) hydrates the map from a local MVF bundle instead of
   * calling Mappedin's cloud API, so it still renders — including on a
   * refresh — with no internet connection. Set VITE_MAP_MODE=online to
   * always load live from Mappedin's cloud instead (requires a working
   * internet connection on every load).
   */
  mapMode: env.VITE_MAP_MODE === "online" ? "online" : "offline",

  /**
   * Local MVF bundle the kiosk hydrates the map from when mapMode is
   * "offline". Fetch/refresh it with `npm run fetch-mvf`. See
   * scripts/fetch-full-mvf.mjs — it's MVF v2, the same format online mode
   * loads, because this venue's v3 export is missing navigation connections.
   */
  mvfBundleUrl: env.VITE_MVF_BUNDLE_URL || "/venue-mvf-v2.zip",
  /** MVF format of that bundle, read from its file name ("…v3…" → 3.0.0, otherwise 2.0.0). */
  mvfBundleVersion: /v3/i.test(env.VITE_MVF_BUNDLE_URL || "") ? ("3.0.0" as const) : ("2.0.0" as const),

  venueName: env.VITE_VENUE_NAME || "25th WPC Energy Congress",

  /** Whether to show the in-header language switcher. Set VITE_SHOW_LANGUAGE_SWITCHER=false to hide it. */
  showLanguageSwitcher: boolOr(env.VITE_SHOW_LANGUAGE_SWITCHER, true),

  /** Header pills — each can be hidden on its own with VITE_SHOW_*=false. All default to shown. */
  showVenueName: boolOr(env.VITE_SHOW_VENUE_NAME, true),
  showClock: boolOr(env.VITE_SHOW_CLOCK, true),
  showCameraDebug: boolOr(env.VITE_SHOW_CAMERA_DEBUG, true),
  showThemeToggle: boolOr(env.VITE_SHOW_THEME_TOGGLE, true),
  showResetButton: boolOr(env.VITE_SHOW_RESET_BUTTON, true),
  showReloadButton: boolOr(env.VITE_SHOW_RELOAD_BUTTON, true),
  /** Big "Where do you want to go?" bar at the bottom centre — an easy-to-spot way into the To field. */
  showBottomSearch: boolOr(env.VITE_SHOW_BOTTOM_SEARCH, true),
  /** Red "Nearest Exit" button (bottom left) — routes to the closest reachable emergency exit. */
  showNearestExit: boolOr(env.VITE_SHOW_NEAREST_EXIT, true),

  /** Whether to show the in-header Online/Offline connection badge. Set VITE_SHOW_CONNECTION_BADGE=false to hide it. */
  showConnectionBadge: boolOr(env.VITE_SHOW_CONNECTION_BADGE, true),

  /** Whether to show the "Share via QR" button on a found route. Set VITE_SHOW_QR_SHARE=true to enable it — off by default. */
  showQrShare: boolOr(env.VITE_SHOW_QR_SHARE, false),

  /**
   * Public origin the QR-share link points to (see `buildShareUrl` in
   * main.ts), instead of `window.location.origin` — which resolves to
   * `localhost` (or a LAN IP) while developing, an address no visitor's
   * phone can actually reach. Defaults to the live kiosk's real domain so
   * the generated QR code always opens correctly regardless of where the
   * kiosk app itself happens to be running from.
   */
  shareBaseUrl: (env.VITE_SHARE_BASE_URL || "https://wayfinder.mirajapps.com").replace(/\/$/, ""),

  /** externalId (set in Mappedin admin) of the Space this physical kiosk sits at. */
  kioskLocationExternalId: (env.VITE_KIOSK_LOCATION_EXTERNAL_ID || "").trim() || null,

  /**
   * External IDs (Space or Point of Interest) of every physical kiosk stand
   * across the venue, comma-separated. Unlike VITE_KIOSK_LOCATION_EXTERNAL_ID
   * (this device's own single spot, different per kiosk), this is the same
   * shared list on every kiosk build — set it in the base .env, not a
   * per-kiosk .env.hallN override — so every device renders the same
   * "here's where all our kiosks are" set of icons, not just its own. Keep
   * it in sync with each kiosk's VITE_KIOSK_LOCATION_EXTERNAL_ID whenever
   * one changes or a new kiosk is added.
   */
  allKioskExternalIds: (env.VITE_ALL_KIOSK_EXTERNAL_IDS || "")
    .split(",")
    .map((id: string) => id.trim())
    .filter(Boolean),

  /**
   * On-screen label identifying which physical kiosk this device/build is
   * (e.g. "Kiosk 3") — useful once several kiosks are deployed across a
   * venue, both for visitors and for confirming the right build landed on
   * the right device. Leave unset (the default) to hide the label entirely.
   */
  kioskLabel: (env.VITE_KIOSK_LABEL || "").trim() || null,

  /**
   * Friendly name for THIS kiosk, shown in the "From" field instead of its
   * External ID (e.g. "Hall 2" rather than "KIOSK-2"). Display only — the map
   * still finds the kiosk's spot by VITE_KIOSK_LOCATION_EXTERNAL_ID. Blank =
   * show the External ID.
   */
  kioskName: (env.VITE_KIOSK_NAME || "").trim() || null,

  /** Whether to show the exhibitor name under each stand's logo marker (see `renderLocationLogos`). Set VITE_HIDE_LOCATION_NAME=true to show the logo alone. */
  hideLocationName: boolOr(env.VITE_HIDE_LOCATION_NAME, false),

  /**
   * Same as `hideLocationName`, but only for categorized locations (amenities
   * like restrooms/F&B/VR area — see `renderLocationLogos`), so their names
   * can be shown while stand names stay hidden, or the other way round. Unset
   * (the default) follows `hideLocationName`.
   */
  hideCategoryLocationName: env.VITE_HIDE_CATEGORY_LOCATION_NAME?.trim()
    ? boolOr(env.VITE_HIDE_CATEGORY_LOCATION_NAME, false)
    : boolOr(env.VITE_HIDE_LOCATION_NAME, false),

  /**
   * Whether to render each stand's own logo marker on the map at all (see
   * `renderLocationLogos`). Set VITE_SHOW_LOCATION_LOGOS=false to turn the
   * feature off entirely — no marker is created and no logo image is ever
   * requested from the browser.
   */
  showLocationLogos: boolOr(env.VITE_SHOW_LOCATION_LOGOS, true),

  /**
   * Whether to paint each stand's own uploaded photo onto its floor
   * footprint at all (see `renderLocationPhotos`). Set
   * VITE_SHOW_LOCATION_PHOTOS=false to turn the feature off entirely — no
   * photo texture is ever requested from the browser.
   */
  showLocationPhotos: photosUrlOverride() ?? boolOr(env.VITE_SHOW_LOCATION_PHOTOS, true),

  /**
   * Shape each stand's logo marker is cropped to (see `renderLocationLogos`):
   * "circle" (default) or "square" (rounded square) both crop the logo into
   * a fixed-size box, while "auto" keeps the logo's own natural aspect
   * ratio uncropped instead — better for logos that aren't roughly square.
   * Set VITE_LOGO_SHAPE to any of the three; anything else falls back to
   * "circle".
   */
  logoShape: oneOf(env.VITE_LOGO_SHAPE, ["circle", "square", "auto"] as const, "circle"),

  /**
   * Padding (px) between a location logo marker's edge and the logo image
   * inside it, at the base marker size (see the `--logo-padding` CSS
   * variable in style.css). It doubles automatically on large displays,
   * matching the marker's own 2x size increase there.
   */
  logoPadding: numberOr(env.VITE_LOGO_PADDING, 4),

  /**
   * While a route starts from "Current location", re-read the visitor's GPS
   * every this-many seconds and recalculate the route from where they are
   * now, so it follows them even when they walk off it. 0 turns it off.
   */
  liveRouteRefreshMs: numberOr(env.VITE_LIVE_ROUTE_REFRESH_SECONDS, 10) * 1000,

  idleTimeoutMs: numberOr(env.VITE_IDLE_TIMEOUT_SECONDS, 90) * 1000,

  /**
   * After a visitor picks From/To from the list, the route starts by itself
   * this many seconds later if they don't press Get Directions. 0 turns it off.
   */
  autoRouteDelayMs: numberOr(env.VITE_AUTO_ROUTE_SECONDS, 5) * 1000,

  /**
   * Arrival effect when the route intro reaches the destination: the
   * stand's walls rise in this colour to this height (meters) over this
   * many milliseconds. See `startDestinationGlow` in mappedin.ts.
   */
  destHighlightColor: (env.VITE_DEST_HIGHLIGHT_COLOR || "").trim() || "#1a73e8",
  destHighlightHeight: numberOr(env.VITE_DEST_HIGHLIGHT_HEIGHT, 3.2),
  destHighlightRiseMs: numberOr(env.VITE_DEST_HIGHLIGHT_RISE_MS, 1400),

  /**
   * Intro/attract video that plays full screen once the kiosk has gone idle — see `showIdleVideo` in
   * main.ts. Path under public/ or a full URL. Set VITE_SHOW_IDLE_VIDEO=false
   * to turn it off and keep the plain idle reset instead.
   */
  idleVideoUrl: boolOr(env.VITE_SHOW_IDLE_VIDEO, true)
    ? ((v) => (v.toLowerCase() === "off" ? null : v || "/videos/intro.mp4"))((env.VITE_IDLE_VIDEO_URL || "").trim())
    : null,
  stepLengthMeters: numberOr(env.VITE_STEP_LENGTH_METERS, 0.75),
  walkingSpeedMps: numberOr(env.VITE_WALKING_SPEED_MPS, 1.2),

  /**
   * Interior stand-wall height as a fraction of their real/default height
   * (0–1). Full-height (1.0) walls block the top-down view into
   * neighboring stands, make the venue feel more closed-in, AND — when a
   * stand's own photo/cap is raised to `standBoxHeight` (a real-meters
   * value, not a fraction of this) — very likely sit far taller than that
   * cap, making it look like it's floating mid-wall instead of sitting on
   * top once the camera is pitched. Tune this fraction to actually match
   * wherever `standBoxHeight` puts the cap, rather than assuming 1.0.
   */
  standWallHeight: numberOr(env.VITE_STAND_WALL_HEIGHT, 0.3),

  /**
   * Fill color for every interior stand wall in the venue. Unlike
   * `standColorRanges` (a per-stand floor tint), this can only be a single
   * color — `WALLS.Interior` is one shared object covering every interior
   * wall at once in the Mappedin SDK, with no way to target one stand's
   * walls individually or vary this per numeric range. Leave blank (the
   * default) to keep the venue's own default wall color untouched. For an
   * actual per-stand colored wall, see `showStandColoredWalls` below
   * instead — a real extruded object per stand, not this shared setting.
   */
  standWallColor: (env.VITE_STAND_WALL_COLOR || "").trim() || null,

  /**
   * When true, each stand's own photo (if it has one) is raised to sit on
   * top like a display case lid — see `standPhotoPosition`/`standBoxHeight`
   * below — rather than flat on the floor. It does NOT force the walls to
   * full height; wall height is always just `standWallHeight` above,
   * whether this is on or off. (An earlier version forced walls to literal
   * 100% height whenever this was true, which is usually much taller than
   * `standBoxHeight` and made a raised photo look like it was floating
   * mid-wall once the camera was pitched — tune `standWallHeight` directly
   * instead to actually match where the cap sits.) Set VITE_STAND_AS_BOX=true
   * to enable — off by default. A boxed stand with no photo of its own
   * (either `showLocationPhotos` is off, or that particular stand has no
   * local photo) gets a plain `standBoxCoverColor` cap raised to the same
   * height instead, so it's never left open-topped — see `applyBoxCover`
   * in mappedin.ts.
   */
  standAsBox: standAsBoxEnabled,

  /**
   * Where a stand's own uploaded photo renders, independent of whether its
   * walls are boxed (`standAsBox`): "flat" places it just above the floor,
   * inside the stand; "cover" raises it to `standBoxHeight` so it sits on
   * top like a display case lid. Set VITE_STAND_PHOTO_POSITION to either —
   * defaults to "cover" when `standAsBox` is on and "flat" when it's off,
   * matching how the two used to be tied together, but either can be set
   * independently (e.g. a flat photo inside full-height walls).
   */
  standPhotoPosition: oneOf(
    env.VITE_STAND_PHOTO_POSITION,
    ["flat", "cover"] as const,
    standAsBoxEnabled ? "cover" : "flat",
  ),

  /**
   * Real-world height (meters) the stand's photo/cover is raised to when
   * `standAsBox` is on, so it sits on top of the box instead of on the
   * floor. Tune to match how tall the boxed stands actually render — this
   * is independent of `standWallHeight`, which is a 0–1 fraction rather
   * than a real measurement. Ignored when `standAsBox` is off.
   */
  standBoxHeight: numberOr(env.VITE_STAND_BOX_HEIGHT, 2.4),

  /**
   * Size multiplier applied to a boxed stand's top layer (its photo or
   * plain cover cap) so it overlaps and touches the box's own walls
   * instead of stopping at the bare floor footprint and leaving a visible
   * gap. 1.0 (the actual floor footprint size) is `standAsBox`'s minimum;
   * raise it if a gap still shows around the cap's edge. Ignored when
   * `standAsBox` is off.
   */
  standBoxCoverScale: numberOr(env.VITE_STAND_BOX_COVER_SCALE, 1.1),

  /**
   * Fill color for the plain cap placed on top of a boxed stand that has no
   * photo of its own (see `standAsBox`). This can't be derived automatically
   * from the venue's own wall/floor material (see `applyBoxCover` in
   * mappedin.ts for why) — set it by eye to whatever shade this venue's
   * actual stand floors/walls render as. Any CSS color works, e.g. a hex
   * like #FFFFFF.
   */
  standBoxCoverColor: env.VITE_STAND_BOX_COVER_COLOR || "#FFFFFF",

  /**
   * Whether individual stand cubes are tinted by which numeric range their
   * own External ID falls into (see `standColorRanges` below). Set
   * VITE_SHOW_STAND_COLOR_RANGES=false to turn this off — every stand keeps
   * its own default color instead. On by default.
   */
  showStandColorRanges: boolOr(env.VITE_SHOW_STAND_COLOR_RANGES, true),

  /**
   * Stand External IDs tinted per color — a comma-separated list of
   * targets, where several consecutive ones can share a color by leaving
   * it off every one but the last: e.g.
   * "1000-2000,101,107,F&B AREA HALL 2:#489FA8,2001-3000:#209056" gives
   * the range 1000-2000, single stands 101 and 107, AND the named location
   * "F&B AREA HALL 2" all the same color, while 2001-3000 gets its own.
   * Each target is a numeric range ("from-to", both ends inclusive), a
   * single stand number, or any other string, matched as a literal
   * External ID (for a named location that isn't in this venue's plain
   * numbering — must match exactly what's set in Mappedin admin). A
   * stand/location whose External ID doesn't appear in any target here is
   * left at its default color. As many targets as needed can be added —
   * see `applyStandColorRanges` in mappedin.ts. Ignored entirely when
   * `showStandColorRanges` is off.
   */
  standColorRanges: parseRangeColorPairs(env.VITE_STAND_COLOR_RANGES),

  /**
   * Whether each stand also gets an actual extruded 3D `Shape` traced from
   * its own real floor polygon, with true vertical walls AND a top face
   * both colored in its own `standColorRanges` color — a REAL colored
   * wall, unlike `standWallColor` (one shared color for literally every
   * wall in the venue) or a flat `Image3D` layer (which can only lie flat,
   * never stand upright). `mapView.Shapes.add()` creates one independent
   * object per call instead of sharing a single global state the way
   * `WALLS.Interior` does, which is what makes per-stand color possible
   * here at all. See `applyStandColoredWall` in mappedin.ts. Off by
   * default; set VITE_SHOW_STAND_COLORED_WALLS=true to enable. Ignored for
   * a stand with no matching range color, or one that has its own photo
   * (see `applyStandColorRanges`) — reserved for the fallback case, same
   * as the floor tint.
   */
  showStandColoredWalls: boolOr(env.VITE_SHOW_STAND_COLORED_WALLS, false),

  /**
   * Real-world distance (meters) the colored wall shape above extends
   * past the stand's own exact floor boundary. Needed because a shape
   * sized to that boundary exactly renders hidden behind the real (and
   * thicker) `WALLS.Interior` geometry sitting right in front of it —
   * visible only at a door gap, where there's no wall to hide behind.
   * Pushing the shape's own edge out past the real wall's outer face
   * fixes that.
   *
   * This is one value for every edge of every stand — there's no signal
   * here to tell "this edge faces an open aisle" apart from "this edge is
   * shared with a neighboring stand", so it's a tradeoff: too large and
   * two adjacent stands' colored walls meet or overlap in the middle,
   * erasing the visible gap between them; too small and it may not fully
   * clear a genuinely thick perimeter wall. Defaults low to protect that
   * gap between neighboring stands.
   */
  standColoredWallOutset: numberOr(env.VITE_STAND_COLORED_WALL_OUTSET, 0.08),

  /**
   * Real-world height (meters) of the colored wall shape — independent of
   * `standBoxHeight` (used for a stand's cap/photo) on purpose. The real
   * `WALLS.Interior` geometry's actual height is unknown here (it comes
   * from the venue's own source data, not anything this app sets in real
   * meters), so this needs tuning by eye until it visibly exceeds the real
   * wall's own height — too short and the real wall's gray top rim still
   * pokes out above the colored one.
   */
  standColoredWallHeight: numberOr(env.VITE_STAND_COLORED_WALL_HEIGHT, 3),

  /**
   * Fallback fill color for a location that has a category assigned in
   * Mappedin admin (a restroom, cafeteria, prayer room, or VR area, per
   * this venue's data — real exhibitor stands are never categorized, see
   * `renderLocationLogos`'s own `has-category` comment) but whose specific
   * category isn't listed in `categoryColors` below. Gets the same box
   * treatment as a `standColorRanges` match — colored floor plus, when
   * `showStandColoredWalls` is on, a colored wall `Shape` too — but keyed
   * by category instead of a numeric External ID range, so it also
   * applies to a location with no numeric ID at all (most amenities have
   * none). Takes priority over a `standColorRanges` match for a location
   * that happens to have both. Leave blank (the default) to disable
   * category-based coloring entirely, including `categoryColors` — see
   * `applyStandColorRanges` in mappedin.ts.
   */
  categoryStandColor: (env.VITE_CATEGORY_STAND_COLOR || "").trim() || null,

  /**
   * Per-category color overrides — comma-separated "Category Name:color"
   * pairs, e.g. "Special Area:#FFFFFF". A location whose category matches
   * one of these gets that color instead of the general
   * `categoryStandColor` fallback above. The name must match exactly what
   * Mappedin admin shows under Locations → Categories. Ignored entirely
   * when `categoryStandColor` is blank (that's the master on/off switch
   * for category-based coloring).
   */
  categoryColors: parseNameColorPairs(env.VITE_CATEGORY_COLORS),

  /**
   * Whether a colored stand (range or category match) also gets a thin
   * bordering line traced around its own floor polygon, at the same
   * height as its colored wall top. Two directly adjacent stands sharing
   * the exact same color (e.g. both in the same hall's range) would
   * otherwise blend into one continuous colored area with no visible
   * boundary between them — this line fixes that, on every stand, not
   * just the specific pairs that happen to share a color. See
   * `applyStandBorderLine` in mappedin.ts. Only used when
   * `showStandColoredWalls` is on (it's drawn at that same top height).
   */
  standBorderLineColor: (env.VITE_STAND_BORDER_LINE_COLOR || "").trim() || null,

  /** Real-world width (meters) of that border line. */
  standBorderLineWidth: numberOr(env.VITE_STAND_BORDER_LINE_WIDTH, 0.05),

  /** Route gradient colors (order: orange, yellow-gold, purple, sky blue, teal) */
  routeGradientColors: [
    env.VITE_ROUTE_GRADIENT_C0 || "#F5841F",
    env.VITE_ROUTE_GRADIENT_C1 || "#F2C230",
    env.VITE_ROUTE_GRADIENT_C3 || "#6B4E9E",
    env.VITE_ROUTE_GRADIENT_C4 || "#3AA9D6",
    env.VITE_ROUTE_GRADIENT_C5 || "#2BB673",
  ],

  /**
   * Fixed default camera orientation for this physical kiosk. The venue
   * center/zoom-to-fit is still computed live from the map geometry (never
   * hardcoded), but bearing/pitch/zoom are pinned to these values — set per
   * device so its default view matches how it's physically mounted, and
   * consistent across refreshes instead of drifting to whatever the SDK's
   * own auto-fit produces. Any left blank falls back to the previous
   * auto-computed behavior for that axis.
   */
  defaultCameraBearing: numberOrNull(env.VITE_DEFAULT_CAMERA_BEARING),
  defaultCameraPitch: numberOrNull(env.VITE_DEFAULT_CAMERA_PITCH),
  defaultCameraZoom: numberOrNull(env.VITE_DEFAULT_CAMERA_ZOOM),

  /** When true, the kiosk boots already rotation-locked to the fixed default view above, instead of requiring an operator to lock it manually each time. */
  defaultCameraLock: boolOr(env.VITE_DEFAULT_CAMERA_LOCK, false),

} as const;
